import React, { useState } from 'react'
import { Button, Modal, Form, Input, Typography, Divider } from 'antd'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import Editor from 'ckeditor5-custom-build/build/ckeditor'

const { Title } = Typography

// Clicksign rejects signer names that aren't a plain full name (letters only,
// at least first and last name) with a "name não está em um formato válido" error.
const SIGNER_NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ']+(?:[-\s][A-Za-zÀ-ÖØ-öø-ÿ']+)+$/

/**
 * Modal component for updating document signer information
 *
 * @component
 * @param {Object} props
 * @param {Function} props.handleEditSigners - Handler function called with updated signing parties data
 * @param {Function} props.handleCancel - Handler function to close the modal
 * @param {boolean} props.showModal - Controls modal visibility
 * @param {Array} props.signingParties - Array of signing party objects containing signer information
 * @param {string} props.signingParties[].title - Title of the signing party group
 * @param {Array} props.signingParties[].signers - Array of signer objects
 * @param {string} props.signingParties[].signers[].title - Title/name of the signer
 * @param {Array} props.signingParties[].signers[].fields - Array of field objects for the signer
 * @param {string} props.signingParties[].signers[].fields[].value - Field type ('Nome' or 'Email')
 * @param {string} props.signingParties[].signers[].fields[].variable - Variable name for the field
 * @param {string} props.signingParties[].signers[].fields[].type - Input type for the field
 * @returns {React.ReactElement|null} Returns modal component or null if no signing parties
 */
const UpdateSignersFormModal = ({
	handleEditSigners,
	handleCancel,
	showModal,
	signingParties,
	message,
	responsibleGroup,
}) => {
	const [form] = Form.useForm()

	/**
	 * Handles form submission by updating signer fields and calling edit handler
	 *
	 * 1. Creates deep copy of signing parties to avoid mutating props
	 * 2. Updates name and email field values from form for each signer
	 * 3. Calls parent handler with updated signing parties
	 * 4. Closes modal via handleCancel
	 */

	const previousMessage = DOMPurify.sanitize(message)
	const [editorData, setEditorData] = useState(previousMessage)
	const [responsibleGroupState, setResponsibleGroupState] =
		useState(responsibleGroup)

	const handleResponsibleChange = (index, field, value) => {
		setResponsibleGroupState((prevState) => {
			const updatedGroup = [...prevState]
			updatedGroup[index] = { ...updatedGroup[index], [field]: value }
			return updatedGroup
		})
	}

	const handleFormSubmit = () => {
		form
			.validateFields()
			.then(() => {
				handleCancel()
				const updatedSigningParties = JSON.parse(
					JSON.stringify(signingParties)
				)
				updatedSigningParties.forEach((signingParty) => {
					signingParty.signers.forEach((signer) => {
						signer.fields.forEach((field) => {
							if (field.value === 'Nome' || field.value === 'Email') {
								field.valueVariable = form.getFieldValue(field.variable)
							}
						})
					})
				})
				// const updatedMessage = form.getFieldValue('message')
				const updatedsigners = {
					message: editorData,
					signers: updatedSigningParties,
					responsible_group: responsibleGroupState,
				}
				handleEditSigners(updatedsigners)
			})
			.catch(() => {})
	}
	// Only render if signingParties exists and has parties
	if (!signingParties?.length) return null

	return (
		<Modal
			open={showModal}
			onCancel={handleCancel}
			maskClosable={true}
			mask={true}
			title={<Title level={3}>Signatários</Title>}
			footer={[
				<Button key="cancelar" onClick={handleCancel}>
					Cancelar
				</Button>,
				<Button key="salvar" type="primary" onClick={handleFormSubmit}>
					Salvar
				</Button>,
			]}>
			<Form form={form} id="assignForm" layout="horizontal">
				<div
					style={{
						flex: 1,
						overflowY: 'scroll',
						padding: '0 15px 0 15px',
						height: '60vh',
					}}>
					{responsibleGroup.length > 0 && (
						<>
							<Title level={4}>Responsáveis</Title>
							{responsibleGroupState?.map((member, index) => (
								<div key={index} style={{ padding: '10px 0' }}>
									<Title level={5}>{`Responsável ${index + 1}`}</Title>
									<Form.Item label="Nome" required>
										<Input
											value={member.name}
											onChange={(e) =>
												handleResponsibleChange(index, 'name', e.target.value)
											}
											placeholder="Nome do responsável"
											rules={[
												{
													required: true,
													message: 'Preencha o nome completo do responsável',
												},
												{
													pattern: /^\S+(?: \S+)+$/,
													message: 'Porfavor preencha com nome e sobrenome',
												},
											]}
										/>
									</Form.Item>
									<Form.Item label="Email" required>
										<Input
											type="email"
											value={member.email}
											onChange={(e) =>
												handleResponsibleChange(index, 'email', e.target.value)
											}
											placeholder="Email do responsável"
										/>
									</Form.Item>
									<Divider />
								</div>
							))}
						</>
					)}

					{signingParties?.map((signingParty, partyIndex) => (
						<div id={`${signingParty.title}-container`} key={partyIndex}>
							{partyIndex > 0 && <Divider />}
							<Title level={4}>{signingParty.title}</Title>
							{signingParty.signers.map((signer, signerIndex) => (
								<div
									id={`${signingParty.title}-${signer.title}-container`}
									key={`${partyIndex}-${signerIndex}`}
									style={{ margin: '10px 0 10px 10px' }}>
									<Title level={5}>{signer.title}</Title>
									{signer.fields.map(
										(field, fieldIndex) =>
											(field.value === 'Nome' || field.value === 'Email') && (
												<Form.Item
													key={`${partyIndex}-${signerIndex}-${fieldIndex}`}
													name={field.variable}
													label={field.value}
													initialValue={field.valueVariable}
													style={{ margin: '10px 0 10px 10px' }}
													rules={[
														signer.required !== false && { required: true, message: `Campo obrigatório` },
														field.value === 'Nome' && {
															pattern: SIGNER_NAME_PATTERN,
															message:
																'Preencha com nome e sobrenome, usando apenas letras',
														},
													]}>
													<Input
														type={field.type}
														placeholder={`Digite aqui o ${field.value} do signatário`}
													/>
												</Form.Item>
											)
									)}
								</div>
							))}
						</div>
					))}

					{message !== '' && (
						<div style={{ marginTop: 30, height: '300px' }}>
							<Title level={4}>Mensagem</Title>
							<CKEditor
								editor={Editor}
								config={{
									toolbar: ['bold', 'italic', 'underline'],
								}}
								data={editorData}
								onChange={(event, editor) => {
									setEditorData(editor.getData())
								}}
							/>
						</div>
					)}
				</div>
			</Form>
		</Modal>
	)
}

UpdateSignersFormModal.propTypes = {
	handleEditSigners: PropTypes.func.isRequired,
	handleCancel: PropTypes.func,
	showModal: PropTypes.bool,
	signingParties: PropTypes.array,
	message: PropTypes.string,
	responsibleGroup: PropTypes.array,
}

UpdateSignersFormModal.defaultProps = {
	signingParties: [],
	message: '',
	responsibleGroup: [],
}

export default UpdateSignersFormModal
